import { inject, observer } from "mobx-react";
import React, { FC, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Loading from "../../components/Loading";
import { MessageResultState, ServiceResult } from "../../types/general-types";
import "./emergency-sms.css";
import Store from "./store";
import {
  GetEmergencyQuestionOptionDto,
  PublishQuestionCreateAnswerDto,
} from "./types";

interface IPage {
  EmergencyPublishQuestionAnswerStore?: Store;
}

const Page: FC<IPage> = inject("EmergencyPublishQuestionAnswerStore")(
  observer((props: IPage) => {
    const { EmergencyPublishQuestionAnswerStore: store } = props;

    const [wrapper, setWrapper] = useState<
      "form" | "error" | "success" | "none"
    >("none");
    const [errorMessage, setErrorMessage] = useState("");
    const key = window.location.search.substring(3);
    const [result, setResult] = useState<GetEmergencyQuestionOptionDto>();
    const [disable, setDisable] = useState(false);
    const [inputDatas, setInputDatas] =
      useState<PublishQuestionCreateAnswerDto>({
        hashedKey: key,
        id: 0,
        questionOptionId: 0,
        freeText: "",
        cancelSmsSending: false,
        lat: "",
        lng: "",
        userIp: "",
        editableAnswer: false,
      });

    // Mobil kontrolü
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
      const checkMobile = () => setIsMobile(window.innerWidth <= 768);
      checkMobile();
      window.addEventListener("resize", checkMobile);
      return () => window.removeEventListener("resize", checkMobile);
    }, []);

    // Konum alınıyor
    useEffect(() => {
      navigator.geolocation.getCurrentPosition((pos) => {
        setInputDatas((d) => ({
          ...d,
          lat: pos.coords.latitude.toString(),
          lng: pos.coords.longitude.toString(),
        }));
      });
    }, []);

    // İlk veri çekme
    useEffect(() => {
      store!
        .getSafeStatus(key)
        .then((data: ServiceResult<GetEmergencyQuestionOptionDto>) => {
          if (data.state === MessageResultState.SUCCESS) {
            const r = data.result;
            if (r.answer) {
              setInputDatas((d) => ({
                ...d,
                id: r.answer.id,
                questionOptionId: r.answer.questionOptionId,
                freeText: r.answer.freeText,
                cancelSmsSending: r.cancelSmsSending,
                editableAnswer: r.editableAnswer,
              }));
            }
            setResult(data.result);
            if (r.answer && !r.editableAnswer) {
              setWrapper("success");
            } else {
              setWrapper("form");
            }
          } else {
            setErrorMessage(data.message);
            setWrapper("error");
          }
        });
    }, [key, store]);

    const successWrapper = (
      <div className="em-success-wrapper">
        <svg
          className="em-success-icon"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="12"
            cy="12"
            r="10.5"
            fill="rgba(38, 224, 127, 0.1)"
            stroke="rgba(38, 224, 127, 0.4)"
            strokeWidth="2"
          />
          <path
            d="M9 12l2 2 4-4"
            stroke="#26E07F"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>

        <div className="em-success-text">
          {result?.language.toLowerCase().startsWith("en")
            ? result?.emergencyPublishQuestionOptions.find(
                (x) => x.id === inputDatas.questionOptionId
              )?.optionDescriptionEN
            : result?.emergencyPublishQuestionOptions.find(
                (x) => x.id === inputDatas.questionOptionId
              )?.optionDescriptionTR}
        </div>
      </div>
    );

    const errorWrapper = (
      <div className="em-error-wrapper">
        <svg
          className="em-error-icon"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="12"
            cy="12"
            r="10.5"
            fill="rgba(220, 53, 69, 0.1)"
            stroke="rgba(220, 53, 69, 0.4)"
            strokeWidth="2"
          />
          <path
            d="M15 9l-6 6M9 9l6 6"
            stroke="#dc3545"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <p className="em-error-message">{errorMessage}</p>
      </div>
    );

    const noneWrapper = <div />;

    const sideFlex: React.CSSProperties = { flex: 1 };
    // --- Form görünümü ---
    const formWrapper = useMemo(
      () => (
        <div>
          {/* Alert */}
          {/* Alert yalnızca yeni form gösterilirken */}
          {!(inputDatas.id > 0 && !inputDatas.editableAnswer) && (
            <div className="em-info-alert">
              {result?.language.toLowerCase().startsWith("en")
                ? `Dear ${result?.personnelName}, ${result?.questionContentEN}`
                : `Sayın ${result?.personnelName}, ${result?.questionContentTR}`}
            </div>
          )}

          {/* Daha önce cevap girilmişse ve düzenleme kapalıysa */}

          <div className="row em-form-row">
            {/* Başlık */}
            <div className="col-12 text-center mb-4">
              <div className="em-form-title">
                {result?.language.toLowerCase().startsWith("en")
                  ? "Are you safe?"
                  : "Güvende misiniz?"}
              </div>
            </div>

            <div className="col-12 mb-2 em-choice-text">
              {result?.language.toLowerCase().startsWith("en")
                ? "Make a choice"
                : "Lütfen seçim yapın"}
            </div>

            <div className="col-12 mb-2">
              {result?.emergencyPublishQuestionOptions.map((item) => (
                <label
                  key={item.id}
                  className={
                    "em-radio-option" +
                    (inputDatas.questionOptionId === item.id ? " selected" : "")
                  }
                >
                  <input
                    type="radio"
                    name="safetyStatus"
                    checked={inputDatas.questionOptionId === item.id}
                    onChange={() =>
                      setInputDatas((d) => ({
                        ...d,
                        questionOptionId:
                          d.questionOptionId === item.id ? 0 : item.id,
                      }))
                    }
                    className="em-radio-input"
                  />
                  <span className="em-radio-label">
                    {result?.language.toLowerCase().startsWith("en")
                      ? item.optionEN
                      : item.optionTR}
                  </span>
                </label>
              ))}
            </div>

            {/* Ek bilgi textarea */}
            <div className="col-12 mb-3 text-left">
              <label htmlFor="freeText">
                {result?.language.toLowerCase().startsWith("en")
                  ? "Want To Add"
                  : "Varsa iletmek istedikleriniz"}
              </label>
              <textarea
                id="freeText"
                className="form-control"
                rows={2}
                placeholder={
                  result?.language.toLowerCase().startsWith("en")
                    ? "Optional: add details here..."
                    : "(İsteğe bağlı) Ek bilgi vermek için buraya yazınız."
                }
                value={inputDatas.freeText}
                onChange={(e) =>
                  setInputDatas((d) => ({ ...d, freeText: e.target.value }))
                }
              />
            </div>

            {/* Gönder butonu (solda hizalı) */}
            <div className="col-12 text-left">
              <button
                className="btn em-btn-submit"
                disabled={disable}
                onClick={async () => {
                  setDisable(true);
                  if (inputDatas.questionOptionId === 0) {
                    const warningMessage: string = result?.language
                      .toLowerCase()
                      .startsWith("en")
                      ? "Could not be saved"
                      : "Kaydedilemedi";
                    toast.warning(warningMessage);
                    return;
                  }
                  const res = await store!.save(inputDatas);
                  if (res.state === MessageResultState.SUCCESS) {
                    setWrapper("success");
                  } else {
                    setErrorMessage(res.message);
                    setWrapper("error");
                  }
                }}
              >
                {result?.language.toLowerCase().startsWith("en")
                  ? "Send"
                  : "Gönder"}
              </button>
            </div>
          </div>
        </div>
      ),
      [
        inputDatas,
        result?.language,
        result?.personnelName,
        result?.questionContentEN,
        result?.questionContentTR,
        result?.emergencyPublishQuestionOptions,
        disable,
        store,
      ]
    );

    return (
      <div className="em-outer">
        {/* navbar */}
        {wrapper !== "success" ? (
          <div className="em-header">
            <div className="em-logo">
              <div style={{ display: "flex" }}>
                <span className="em-dot" />
                <span className="em-dot" />
                <span className="em-dot" />
              </div>
              <span className="em-brand">Securitas</span>
            </div>
          </div>
        ) : (
          <></>
        )}
        {/* Ana içerik */}
        <div className="em-page-container">
          {!isMobile && <div className="em-side-flex" />}
          <div className="em-card">
            {wrapper === "none" ? (
              <Loading />
            ) : wrapper === "form" ? (
              formWrapper
            ) : wrapper === "success" ? (
              successWrapper
            ) : wrapper === "error" ? (
              errorWrapper
            ) : (
              noneWrapper
            )}
          </div>
          {!isMobile && <div style={sideFlex} />}
        </div>
        {/* Footer */}
        <div className="em-footer">
          {result?.language.toLowerCase().startsWith("en")
            ? "This web page has been prepared by Securitas Güvenlik Hizmetleri A.Ş. for communication in emergencies."
            : "Bu web sayfası Securitas Güvenlik Hizmetleri A.Ş. tarafından acil durumlarda iletişim için hazırlanmıştır."}
        </div>
      </div>
    );
  })
);

export default Page;
